import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from "react";

export type LangCode = "vi" | "en" | "lo";

interface LanguageContextType {
  lang: LangCode;
  setLang: (lang: LangCode) => void;
  t: (key: string) => string;
  dict: Record<string, string>;
}

const LanguageContext = createContext<LanguageContextType | null>(null);

const cache: Record<string, Record<string, string>> = {};

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [lang, setLang] = useState<LangCode>("vi");
  const [dict, setDict] = useState<Record<string, string>>({});

  // load lang từ localStorage
  useEffect(() => {
    const saved = localStorage.getItem("lang") as LangCode | null;
    if (saved) {
      setLang(saved);
    }
  }, []);

  // lưu lang
  useEffect(() => {
    localStorage.setItem("lang", lang);
  }, [lang]);

  // load dict theo lang
  useEffect(() => {
    let cancelled = false;

    if (cache[lang]) {
      setDict(cache[lang]);
      return;
    }

    fetch(`/string/${lang}.json`)
      .then((r) => r.json())
      .then((data) => {
        cache[lang] = data;
        if (!cancelled) setDict(data);
      })
      .catch(() => {
        if (!cancelled) setDict({});
      });

    return () => {
      cancelled = true;
    };
  }, [lang]);

  const t = useCallback(
    (key: string) => {
      return dict[key] ?? key;
    },
    [dict]
  );

  const value = useMemo(
    () => ({
      lang,
      setLang,
      t,
      dict,
    }),
    [lang, t, dict]
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    throw new Error("useLanguage must be used inside LanguageProvider");
  }
  return ctx;
}